import React, { useState, useCallback, useEffect } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useDispatch, useSelector } from 'react-redux';
import CommonPagination from 'components/commonPagination/CommonPagination';
import CommonTable from 'components/commonTable/CommonTable';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import moment from 'moment';
import * as dateFns from 'date-fns';
import EInvoiceListFilter from './EInvoiceListFilter';
import PropTypes from 'prop-types';
import { ListEInvoiceData } from 'reactRedux/thunks/eInvoiceThunk/eInvoiceThunk';
import useListState from 'hooks/useListState';

const EInvoiceList = ({ invoiceType, isActive }) => {
  const dispatch = useDispatch();
  const { tabletextColor } = useColorModeVariables();

  // Each tab stores its state under a namespaced prefix so B2B and B2C
  // can coexist in the same URL without overwriting each other.
  const urlPrefix = invoiceType === 'B2B' ? 'b2b_' : 'b2c_';

  const {
    params,
    setParam,
    setParams,
    debouncedParams,
    page,
    setPage,
    resetAll,
  } = useListState({
    defaults: {
      region: '',
      invoiceId: '',
      transactionId: '',
      merchantName: '',
      limit: 10,
    },
    debounceKeys: ['invoiceId', 'transactionId', 'merchantName'],
    prefix: urlPrefix,
  });

  // Date range states stay local — complex date-object state not suited for URL
  const [dateRange, setDateRange] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [tempDateRange, setTempDateRange] = useState([
    {
      endDate: new Date(),
      startDate: dateFns.addDays(new Date(), -6),
      key: 'selection',
    },
  ]);
  const [calendarFilterTitle, setCalendarFilterTitle] = useState('Select Date');
  const [tempCalendarFilterTitle, setTempCalendarFilterTitle] =
    useState('Last 7 days');

  const { isLoading, eInvoiceListData } = useSelector(
    (state) => state.EInvoiceSlice,
  );

  const columnHelper = createColumnHelper();

  // ── Date range handlers ────────────────────────────────────────────────────

  const handleSelect = (ranges) => {
    const customStaticRanges = [
      {
        label: 'Today',
        range: () => ({ startDate: new Date(), endDate: new Date() }),
      },
      {
        label: 'Last 7 days',
        range: () => ({
          startDate: dateFns.addDays(new Date(), -6),
          endDate: new Date(),
        }),
      },
      {
        label: 'Last 30 days',
        range: () => ({
          startDate: dateFns.addDays(new Date(), -29),
          endDate: new Date(),
        }),
      },
    ];

    const selectedRange = customStaticRanges.find(
      (range) =>
        range.range().startDate.getTime() ===
          ranges.selection.startDate.getTime() &&
        range.range().endDate.getTime() === ranges.selection.endDate.getTime(),
    );

    setTempCalendarFilterTitle(selectedRange?.label || 'Custom');
    setTempDateRange([ranges.selection]);
  };

  const handleApply = async () => {
    setPage(1);
    setDateRange(tempDateRange);
    setCalendarFilterTitle(tempCalendarFilterTitle);
    setShowCalendar(false);
  };

  const handleClear = () => {
    const defaultRange = [
      {
        endDate: new Date(),
        startDate: dateFns.addDays(new Date(), -6),
        key: 'selection',
      },
    ];
    setTempDateRange(defaultRange);
    setDateRange(null);
    setCalendarFilterTitle('Select Date');
    setTempCalendarFilterTitle('Last 7 days');
    setShowCalendar(false);
  };

  const handleReset = () => {
    resetAll();
    const defaultRange = [
      {
        endDate: new Date(),
        startDate: dateFns.addDays(new Date(), -6),
        key: 'selection',
      },
    ];
    setTempDateRange(defaultRange);
    setDateRange(null);
    setCalendarFilterTitle('Select Date');
    setTempCalendarFilterTitle('Last 7 days');
    setShowCalendar(false);
  };

  // ── API fetch ──────────────────────────────────────────────────────────────

  const getFormattedDates = (currentDateRange) => {
    if (!currentDateRange || !currentDateRange[0]) {
      return { start: null, end: null };
    }
    const start = dateFns.format(currentDateRange[0]?.startDate, 'yyyy-MM-dd');
    const end = dateFns.format(currentDateRange[0]?.endDate, 'yyyy-MM-dd');
    return { start, end };
  };

  const getInvoiceList = useCallback(() => {
    const { start, end } = getFormattedDates(dateRange);

    dispatch(
      ListEInvoiceData({
        page,
        limit: params.limit || 10,
        region: params.region,
        invoiceId: debouncedParams.invoiceId,
        transactionId: debouncedParams.transactionId,
        merchantName: debouncedParams.merchantName,
        invoiceType,
        start,
        end,
      }),
    );
  }, [
    dispatch,
    page,
    params.limit,
    params.region,
    debouncedParams.invoiceId,
    debouncedParams.transactionId,
    debouncedParams.merchantName,
    invoiceType,
    dateRange,
  ]);

  useEffect(() => {
    if (isActive) {
      getInvoiceList();
    }
  }, [getInvoiceList, isActive]);

  // ── Table columns ──────────────────────────────────────────────────────────

  // B2B Merchant Columns
  const merchantColumns = [
    columnHelper.accessor('regionName', {
      id: 'regionName',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Region
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" fontWeight="700">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('merchantId', {
      id: 'merchantId',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Merchant ID
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" fontWeight="700">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('name', {
      id: 'name',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Merchant Name
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" fontWeight="700">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('commissionValue', {
      id: 'commissionValue',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Commission (Gross)
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" fontWeight="700">
          {info.getValue() !== undefined
            ? `${info.row.original.currencySymbol ?? '€'}${info.getValue()}`
            : '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('year', {
      id: 'year',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Invoice Period Year
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" fontWeight="700">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor(
      (row) => {
        if (row.quarter) return `Q${row.quarter}`;
        if (row.month) return `M${row.month}`;
        return null;
      },
      {
        id: 'invoicePeriod',
        header: () => (
          <Text
            justifyContent="space-between"
            align="center"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
          >
            Invoice Period
          </Text>
        ),
        cell: (info) => (
          <Text color={tabletextColor} fontSize="sm" fontWeight="700">
            {info.getValue() || '-'}
          </Text>
        ),
      },
    ),
    columnHelper.accessor('invoiceId', {
      id: 'invoiceId',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Invoice ID
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text
            color={tabletextColor}
            fontSize="sm"
            fontWeight="700"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {info.getValue() || '-'}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Created Date
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" fontWeight="700">
          {info.getValue() ? moment(info.getValue()).format('DD/MM/YYYY') : '-'}
        </Text>
      ),
    }),
  ];

  // B2C Customer Columns
  const customerColumns = [
    columnHelper.accessor('invoiceId', {
      id: 'invoiceId',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Invoice ID
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text
            color={tabletextColor}
            fontSize="sm"
            fontWeight="700"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {info.getValue() || '-'}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('stripeId', {
      id: 'stripeId',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Transaction ID
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="700"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('einvoiceTime', {
      id: 'einvoiceTime',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Invoice Date Time
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" fontWeight="700">
          {info.getValue() ? moment(info.getValue()).format('DD/MM/YYYY') : '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('amount', {
      id: 'amount',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Amount
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" fontWeight="700">
          {info.getValue() !== undefined
            ? `${info.row.original.currencySymbol ?? '€'}${info.getValue()}`
            : '-'}
        </Text>
      ),
    }),
    columnHelper.accessor(
      (row) => `${row.firstname || ''} ${row.lastname || ''}`.trim(),
      {
        id: 'userName',
        header: () => (
          <Text
            justifyContent="space-between"
            align="center"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
          >
            User Name
          </Text>
        ),
        cell: (info) => (
          <Text color={tabletextColor} fontSize="sm" fontWeight="700">
            {info.getValue() || '-'}
          </Text>
        ),
      },
    ),
    columnHelper.accessor('regionName', {
      id: 'regionName',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Region
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" fontWeight="700">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('merchantName', {
      id: 'merchantName',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Merchant Name
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="700"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {info.getValue() || '-'}
        </Text>
      ),
    }),
  ];

  const columns = invoiceType === 'B2B' ? merchantColumns : customerColumns;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      <EInvoiceListFilter
        region={params.region}
        setRegion={(val) => setParam('region', val)}
        invoiceId={params.invoiceId}
        setInvoiceId={(val) => setParam('invoiceId', val)}
        transactionId={params.transactionId}
        setTransactionId={(val) => setParam('transactionId', val)}
        merchantName={params.merchantName}
        setMerchantName={(val) => setParam('merchantName', val)}
        setPage={setPage}
        setShowCalendar={setShowCalendar}
        showCalendar={showCalendar}
        tempDateRange={tempDateRange}
        handleApply={handleApply}
        handleClear={handleClear}
        handleSelect={handleSelect}
        calendarFilterTitle={calendarFilterTitle}
        handleReset={handleReset}
        invoiceType={invoiceType}
      />

      <Flex
        direction="column"
        bgColor="white"
        borderRadius="15px"
        p={{ base: '10px', md: '20px' }}
      >
        <CommonTable
          columns={columns}
          data={eInvoiceListData?.list}
          isLoading={isLoading}
          isLink={true}
          parentLink={
            invoiceType === 'B2B'
              ? '/admin/e-invoices/merchant-details'
              : '/admin/e-invoices/customer-details'
          }
          keyValue="id"
          dynamicWidth={
            invoiceType === 'B2B'
              ? {
                  regionName: '130px',
                  merchantId: '120px',
                  name: '200px',
                  commissionValue: '150px',
                  year: '150px',
                  invoicePeriod: '130px',
                  invoiceId: '300px',
                  createdAt: '130px',
                }
              : {
                  invoiceId: '250px',
                  stripeId: '300px',
                  einvoiceTime: '200px',
                  amount: '100px',
                  userName: '150px',
                  regionName: '100px',
                  merchantName: '300px',
                }
          }
        />

        <CommonPagination
          totalCount={eInvoiceListData?.total}
          activePage={page}
          pageCount={Math.ceil(
            eInvoiceListData?.total / eInvoiceListData?.limit,
          )}
          onPageChange={setPage}
          itemsPerPage={params.limit || 10}
          onItemsPerPageChange={(newLimit) => {
            setParams({ limit: newLimit, page: 1 });
          }}
        />
      </Flex>
    </Box>
  );
};

EInvoiceList.propTypes = {
  invoiceType: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
};

export default EInvoiceList;
